import { useState } from "react";
import axios from "axios";
import { Mail, MapPin, MessageSquare, Send, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      toast.success("Message received. We'll reply within 1 business day.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to send. Please retry.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <section className="py-20 lg:py-28" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-5">
            <p className="label-eyebrow mb-5">Contact</p>
            <h1 className="font-heading text-5xl sm:text-6xl font-bold tracking-tighter leading-[0.95]">
              Say hello.
              <br />
              <span className="text-mint-600 dark:text-mint">We read every message.</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-md leading-relaxed">
              Bug reports, feature requests, security disclosures or just a friendly note.
            </p>

            <div className="mt-10 space-y-5">
              <div className="flex items-start gap-3" data-testid="contact-info-email">
                <span className="h-10 w-10 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                  <Mail className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold">Email</p>
                  <p className="text-sm text-muted-foreground">hello@avault.app</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="contact-info-security">
                <span className="h-10 w-10 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                  <MessageSquare className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold">Security disclosures</p>
                  <p className="text-sm text-muted-foreground">security@avault.app · PGP available</p>
                </div>
              </div>
              <div className="flex items-start gap-3" data-testid="contact-info-location">
                <span className="h-10 w-10 rounded-xl bg-mint/40 dark:bg-mint/20 flex items-center justify-center text-mint-700 dark:text-mint">
                  <MapPin className="h-4 w-4" />
                </span>
                <div>
                  <p className="text-sm font-semibold">HQ</p>
                  <p className="text-sm text-muted-foreground">Distributed · Europe & North America</p>
                </div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              className="glass-card p-8 lg:p-10 space-y-5"
              data-testid="contact-form"
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="name" className="text-xs uppercase tracking-widest">Name</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={update("name")}
                    placeholder="Ada Lovelace"
                    className="mt-2 rounded-xl h-11 bg-background"
                    data-testid="contact-input-name"
                  />
                </div>
                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-widest">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    placeholder="ada@example.com"
                    className="mt-2 rounded-xl h-11 bg-background"
                    data-testid="contact-input-email"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="subject" className="text-xs uppercase tracking-widest">Subject</Label>
                <Input
                  id="subject"
                  value={form.subject}
                  onChange={update("subject")}
                  placeholder="Question about Pro"
                  className="mt-2 rounded-xl h-11 bg-background"
                  data-testid="contact-input-subject"
                />
              </div>
              <div>
                <Label htmlFor="message" className="text-xs uppercase tracking-widest">Message</Label>
                <Textarea
                  id="message"
                  value={form.message}
                  onChange={update("message")}
                  placeholder="Tell us what's on your mind…"
                  rows={6}
                  className="mt-2 rounded-xl bg-background resize-none"
                  data-testid="contact-input-message"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="rounded-full bg-mint text-ink hover:bg-mint-300 px-7 py-6 text-base shadow-lg shadow-mint-500/20"
                data-testid="contact-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
                {loading ? "Sending..." : "Send message"}
              </Button>
              {sent && (
                <p className="text-sm text-mint-700 dark:text-mint" data-testid="contact-success">
                  ✓ Got it — we&apos;ll reply within 1 business day.
                </p>
              )}
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
